import Cross from '@assets/svg/icons/cross.svg'
import { useContentfulInspectorMode } from '@contentful/live-preview/react'
import { ContentType } from '@entries/content-types'
import ContentfulInformationButton from '@entries/contentful-information-button'
import ContentfulInternalLinkLink from '@entries/contentful-internal-link-link'
import { useEntryInspector } from '@hooks/use-entry-inspector'
import { Entry } from '@resolvers/entry'
import { BANNER_SEVERITY } from '@utils/banners'
import clsx from 'clsx/lite'
import React, { useCallback, useEffect, useState } from 'react'

import { useIsIframe } from '../../../plugins/gatsby-plugin-espace-client'

const COOKIE_DURATION_IN_DAYS = 10
const COOKIE_DURATION = COOKIE_DURATION_IN_DAYS * 24 * 60 * 60

const BANNER_COLORS_MAPPER = {
  [BANNER_SEVERITY.BLACK]: {
    text: 'text-[#FFFFFF]',
    bg: 'bg-[rgba(16,25,13,0.5)] backdrop-blur-[27px]',
  },
  [BANNER_SEVERITY.BLUE]: {
    text: 'text-[#FFFFFF]',
    bg: 'bg-[#0765B2]',
  },
  [BANNER_SEVERITY.ORANGE]: {
    text: 'text-[#10190D]',
    bg: 'bg-[#CF6B00]',
  },
  [BANNER_SEVERITY.RED]: {
    text: 'text-[#FFFFFF]',
    bg: 'bg-[#9E243F]',
  },
}
type InformationBannerProps = {
  severity: BANNER_SEVERITY
  text: string
  link: Queries.StaticPageInformationBannerFragment['link']
  bannerId: string
  modificationDate: string
}

export const InformationBanner: React.FC<InformationBannerProps> = ({
  severity,
  text,
  bannerId,
  modificationDate,
  link,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const isIframe = useIsIframe()
  const entryInspectorProps = useEntryInspector({
    contentTemplateId: 'InformationBanner',
    contentTypeId: 'InformationBanner',
    entryId: bannerId,
  })

  const contentfulInspectorProps = useContentfulInspectorMode({
    entryId: bannerId,
  })

  const handleClose = useCallback(() => {
    const cookieInfo = {
      bannerId,
      modificationDate,
    }
    document.cookie = `fortuneoInformationBanner-${bannerId}=${JSON.stringify(cookieInfo)};max-age=${COOKIE_DURATION}`
    setIsOpen(false)
  }, [bannerId, modificationDate])

  useEffect(() => {
    const cookiesString = document.cookie
    if (cookiesString.length === 0) {
      setIsOpen(true)
      return
    }

    const cookies = cookiesString.split('; ')
    const cookiesObject: Record<string, unknown> = {}
    cookies.forEach((c) => {
      if (c.includes('fortuneoInformationBanner')) {
        const cookie = c.split('=')
        cookiesObject[cookie[0]] = JSON.parse(cookie[1])
      }
    })
    if (!cookiesObject[`fortuneoInformationBanner-${bannerId}`]) {
      setIsOpen(true)
      return
    }

    if (
      cookiesObject[`fortuneoInformationBanner-${bannerId}`]
        .modificationDate === modificationDate
    ) {
      setIsOpen(false)
      return
    }

    setIsOpen(true)
  }, [bannerId, modificationDate])

  return (
    <div
      {...entryInspectorProps}
      {...contentfulInspectorProps}
      className={clsx(
        'laptop:top-[72px] absolute inset-x-0 top-14 z-999 w-full',
        BANNER_COLORS_MAPPER[severity].bg,
        BANNER_COLORS_MAPPER[severity].text,
        (isIframe || !isOpen) && 'hidden'
      )}
    >
      <div className="section-content-layout px-default flex min-h-[52px] w-full items-center justify-between py-4">
        <span className="text-085 w-full text-center">
          {text}{' '}
          {link && (
            <div className="inline-flex">
              <Entry
                content={link}
                mapping={{
                  [ContentType.ContentfulInternalLink]:
                    ContentfulInternalLinkLink,
                  [ContentType.ContentfulInformationPanel]:
                    ContentfulInformationButton,
                }}
              />
            </div>
          )}
        </span>
        <button
          className="ml-[12px] h-[24px] w-[24px]"
          type="button"
          onClick={handleClose}
        >
          <Cross
            fill={severity === BANNER_SEVERITY.ORANGE ? '#10190D' : '#FFF'}
            height={10.5}
            width={10.5}
          />
        </button>
      </div>
    </div>
  )
}
